/**
 * Sprint economics
 *
 * What each sprint was estimated to cost in tokens, what it actually cost, and
 * how much of the sprint was estimated at all. This is the screen Sam reviews at
 * the end of a run to see whether work stayed within estimate.
 *
 * The figure that matters most here is the coverage, not the variance. An item
 * nobody estimated spends real money against an estimate of zero, so the variance
 * is drawn from the estimated items only and this says so wherever a sprint is
 * part-estimated. A variance taken from a tenth of a sprint is not a statement
 * about the sprint, and a screen that showed it as one would be worse than no
 * screen.
 *
 * It is scoped to the project whose tab it sits on, for the same reason: a total
 * that quietly included another project's sprints would be a worse lie than the
 * flattering variance this screen was built to avoid.
 */

import { useEffect, useState } from 'react';
import { getAllSprintEconomics, COVERAGE_FLOOR_PCT, type SprintRollup } from '../../../services/sprintService';

const money = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  // The sign goes outside the symbol: "-$3.80", not "$-3.80".
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;
};

/** Over or under, said in a word. Colour alone does not survive greyscale, a
 *  screenshot, or a red-green colourblind reader (Maya). */
const direction = (n: number): string => (n >= 0 ? 'under' : 'over');

/** "1 item" / "2 items" - the screen was reading "0 of 1 items" (Maya, Asha). */
const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);


const tokens = (n: number): string =>
  (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

interface SprintEconomicsProps {
  /** The project whose sprints these are. Required: unscoped totals are wrong. */
  projectId: string;
}

export function SprintEconomics({ projectId }: SprintEconomicsProps) {
  const [rollups, setRollups] = useState<SprintRollup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setRollups(null);
    setError(null);
    setOpen(null);
    getAllSprintEconomics(projectId)
      .then((r) => { if (live) setRollups(r); })
      .catch(() => { if (live) setError('Could not read sprint economics.'); });
    return () => { live = false; };
  }, [projectId]);

  if (error) {
    return (
      <div role="alert" className="bg-white border border-zinc-200 rounded-lg p-4 text-sm text-red-700 dark:bg-zinc-900 dark:border-zinc-800">
        {error}
      </div>
    );
  }
  if (!rollups) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-4 text-sm text-gray-500 dark:bg-zinc-900 dark:border-zinc-800">
        Reading sprint economics…
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
      <h3 className="text-sm font-semibold text-black dark:text-white">Estimate against actual, by sprint</h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        A positive variance is under the estimate. Work that nobody estimated is counted in the spend and
        excluded from the comparison, so the coverage beside each sprint is what says how much the
        variance is worth. Spend is only counted where the run that produced it recorded which item it
        was for, so a sprint with no attributed spend reports that rather than a variance.
      </p>

      {rollups.length === 0 ? (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">This project has no sprint yet.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full table-fixed text-xs">
            <colgroup>
              <col className="w-[28%]" />
              <col className="w-[15%]" />
              <col className="w-[15%]" />
              <col className="w-[21%]" />
              <col className="w-[21%]" />
            </colgroup>
            <thead className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th scope="col" className="py-1.5 pr-3">Sprint</th>
                <th scope="col" className="py-1.5 pr-3 text-right">Estimated</th>
                <th scope="col" className="py-1.5 pr-3 text-right">Actual</th>
                <th scope="col" className="py-1.5 pr-3 text-right">Variance</th>
                <th scope="col" className="py-1.5">Estimate coverage</th>
              </tr>
            </thead>
            <tbody>
              {rollups.map((r) => (
                <tr key={r.sprintId} className="border-t border-zinc-100 align-top dark:border-zinc-800">
                  <td className="py-1.5 pr-3">
                    <button
                      type="button"
                      onClick={() => setOpen(open === r.sprintId ? null : r.sprintId)}
                      aria-expanded={open === r.sprintId}
                      className="text-left font-medium text-black underline-offset-2 hover:underline dark:text-white"
                    >
                      <span aria-hidden="true" className="mr-1 inline-block text-gray-500 dark:text-gray-400">
                        {open === r.sprintId ? '▾' : '▸'}
                      </span>
                      {r.name}
                    </button>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{r.status} · {r.startDate} to {r.endDate}</div>
                  </td>
                  <td className="py-1.5 pr-3 text-right text-black dark:text-white">
                    {money(r.estimated.costUsd)}
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{tokens(r.estimated.totalTokens)} tokens</div>
                  </td>
                  <td className="py-1.5 pr-3 text-right text-black dark:text-white">
                    {money(r.actual.costUsd)}
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{r.actual.sessions} {plural(r.actual.sessions, 'run', 'runs')}</div>
                  </td>
                  <td className="py-1.5 pr-3 text-right">
                    {(() => {
                      // Decided by the rollup, where the figure and the spend it
                      // is drawn from live. Re-deciding it here is what read the
                      // wrong population last time (Bill).
                      const standing = r.varianceStanding;
                      if (standing === 'nothing-estimated') {
                        return <span className="text-gray-500 dark:text-gray-400">nothing estimated</span>;
                      }
                      if (standing === 'no-spend') {
                        return (
                          <>
                            <span className="text-gray-500 dark:text-gray-400">no runs recorded</span>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">
                              nothing is recorded against the estimated items
                            </div>
                          </>
                        );
                      }
                      const tone = standing === 'too-thin'
                        ? 'text-gray-600 dark:text-gray-300'
                        : r.varianceUsd! >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400';
                      // How much of the estimated work has actually run. A figure
                      // backed by one item of ten is not a verdict on the sprint,
                      // and at 100% coverage no other caption fires (Maya MAYA-05).
                      // itemsEstimatedWithSpend, never itemsWithSpend: the latter counts
                      // spend on unestimated work too, and stating it here claimed
                      // three times the evidence that existed (Asha NEW-04).
                      const ran = r.coverage.itemsEstimatedWithSpend;
                      const evidenced = ran < r.coverage.itemsEstimated;
                      return (
                        <>
                          <span className={tone}>
                            {money(r.varianceUsd)} {direction(r.varianceUsd!)}
                          </span>
                          {standing === 'too-thin' ? (
                            <div className="text-[10px] text-amber-700 dark:text-amber-500">
                              only {r.coverage.estimateCoveragePct}% estimated — too little to judge
                            </div>
                          ) : evidenced ? (
                            <div className="text-[10px] text-amber-700 dark:text-amber-500">
                              {r.coverage.itemsEstimated === 1
                                ? 'the one estimated item has no recorded run'
                                : `${ran} of ${r.coverage.itemsEstimated} estimated items ${plural(ran, 'has', 'have')} a recorded run`}
                            </div>
                          ) : r.coverage.varianceIsPartial && (
                            <div className="text-[10px] text-amber-700 dark:text-amber-500">on part of the sprint</div>
                          )}
                        </>
                      );
                    })()}
                  </td>
                  <td className="py-1.5 text-black dark:text-white">
                    <span className={r.coverage.itemsInSprint > 0
                      && r.coverage.estimateCoveragePct < COVERAGE_FLOOR_PCT
                      ? 'font-semibold text-amber-700 dark:text-amber-500' : ''}>
                      {r.coverage.estimateCoveragePct}%
                    </span>
                    {' '}
                    <span className="text-gray-500 dark:text-gray-400">
                      ({r.coverage.itemsEstimated} of {r.coverage.itemsInSprint}{' '}
                      {plural(r.coverage.itemsInSprint, 'item', 'items')})
                    </span>
                    {r.coverage.unestimatedSpendUsd > 0 && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">
                        {money(r.coverage.unestimatedSpendUsd)} spent on work with no estimate
                      </div>
                    )}
                    {r.coverage.itemsMissing > 0 && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">
                        {r.coverage.itemsMissing} named{' '}
                        {plural(r.coverage.itemsMissing, 'item is', 'items are')} no longer in the backlog
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (() => {
        const r = rollups.find((x) => x.sprintId === open);
        if (!r) return null;
        return (
          <div className="mt-4 rounded border border-zinc-200 p-3 dark:border-zinc-800">
            <h4 className="text-xs font-semibold text-black dark:text-white">{r.name} — item by item</h4>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">
              {r.items.length} {plural(r.items.length, 'item', 'items')}
            </p>
            {r.items.length === 0 ? (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {r.coverage.itemsMissing > 0
                  ? 'This sprint names no item that still exists.'
                  : 'No items in this sprint yet.'}
              </p>
            ) : (
            <div className="mt-2 max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="py-1 pr-3">Item</th>
                    <th scope="col" className="py-1 pr-3 text-right">Estimated</th>
                    <th scope="col" className="py-1 pr-3 text-right">Actual</th>
                    <th scope="col" className="py-1 text-right">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {r.items.map((i) => (
                    <tr key={i.id} className="border-t border-zinc-100 dark:border-zinc-800">
                      <td className="py-1 pr-3 text-black dark:text-white">
                        {i.title}
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">{i.status}{i.assignedAgent ? ` · ${i.assignedAgent}` : ''}</div>
                      </td>
                      <td className="py-1 pr-3 text-right text-black dark:text-white">
                        {i.hasEstimate ? money(i.estimated.costUsd) : <span className="text-gray-500 dark:text-gray-400">not estimated</span>}
                      </td>
                      <td className="py-1 pr-3 text-right text-black dark:text-white">{i.actual.sessions > 0 ? money(i.actual.costUsd) : '—'}</td>
                      <td className="py-1 text-right">
                        {i.varianceUsd === null ? (
                          <span className="text-gray-500 dark:text-gray-400">—</span>
                        ) : i.actual.sessions === 0 ? (
                          // Estimated, but nothing recorded against it. Showing the
                          // whole estimate in green would read as money saved (Asha).
                          <span className="text-gray-500 dark:text-gray-400">none recorded</span>
                        ) : (
                          <span className={i.varianceUsd >= 0
                            ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                            {money(i.varianceUsd)} {direction(i.varianceUsd)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default SprintEconomics;
